import { getFeathersClient } from '@/pages/chat/feathers-client'
import { useChatStore } from './chat-store'
import { useUserStore } from './user-store'
import { Channel, ChannelData, ChannelImpl } from '../../../shared-src/channel'
import { PaginatedMessagesResponse } from '../../../shared-src/paginated-messages'
import { MessageService } from '../../../shared-src/message-service'

export async function fetchChannels(params: { userId: string }): Promise<void> {
	const { userId } = params

	try {
		const feathersClient = getFeathersClient()
		const channelsService = feathersClient.service('channels')

		const channelDataList = await channelsService.find({
			query: { userId }
		})
		const channels = channelDataList.map((channelData: ChannelData) => new ChannelImpl(channelData, userId))

		const { setChannels, activeChannelId, setActiveChannelId } = useChatStore.getState()
		setChannels(channels)

		const activeChannel = channels.find((ch: Channel) => ch.id === activeChannelId)
		if (!activeChannel && channels.length > 0)
			setActiveChannelId(channels[0].id)
	} catch (error) {
		//	the server now THROWS when it could not determine the channels, instead of
		//	resolving with [] (ha-370). Reaching this catch therefore means "we do not know
		//	what channels this user has" -- NOT "this user has none" -- so the existing list
		//	is deliberately left untouched. Before the distinction existed, a timeout arrived
		//	as a successful empty response, `setChannels([])` ran, and the sidebar was wiped
		//	with nothing to tell the user or the code that anything had gone wrong.
		//
		//	Do not "recover" by calling setChannels([]) here. Showing an empty sidebar is
		//	precisely the failure this replaced.
		console.error('Could not determine channels (leaving the existing list intact):', error)

		//	Leaving the list untouched is necessary but not sufficient: on the FIRST fetch there is
		//	no list to leave intact, and the initial `[]` reads as "this user has no channels"
		//	(ha-437). Record the failure so the sidebar can draw "unknown" instead of "none".
		//
		//	This looks like it should be conditional on not having loaded already -- it IS, and the
		//	condition lives inside `setChannelsError` so it cannot be forgotten here. `fetchChannels`
		//	also runs on every foreground, and an unconditional write would replace a good sidebar
		//	with an error icon on one failed refetch (ha-370's case A, back again).
		useChatStore.getState().setChannelsError()
	}
}

export async function fetchMessages(params: { channelId: string }): Promise<void> {
	const { channelId } = params

	try {
		const feathersClient = getFeathersClient()
		const messagesService = feathersClient.service('messages') as unknown as MessageService
		const { pageSize, setMessagesForChannel } = useChatStore.getState()

		const response: PaginatedMessagesResponse = await messagesService.loadPage({
			channelId,
			maxCount: pageSize
		})
		setMessagesForChannel(channelId, {
			messages: response.messages,
			hasMore: response.hasMore
		})
	} catch (error) {
		console.error('Failed to fetch messages:', error)
	}
}

export async function fetchChannelDetails(params: { channelId: string }): Promise<void> {
	const { channelId } = params

	try {
		const feathersClient = getFeathersClient()
		const channelService = feathersClient.service('channels')

		const channelData = await channelService.get(channelId)
		const userId = useUserStore.getState().user?.userId
		const channel = new ChannelImpl(channelData, userId)

		const { updateChannel } = useChatStore.getState()
		updateChannel(channel)
	} catch (error) {
		//	403 means the backend deliberately refused: the user was removed from the group, is not a
		//	participant in the DM, or cannot access that bot channel -- so this channel is
		//	legitimately not readable by them. That is expected and
		//	the UI already copes (it redirects, or renders without details), so logging it as an error
		//	made correct behaviour look like a fault -- for months (ha-323). Anything else is a real
		//	failure and stays an error.
		if ((error as { code?: number })?.code === 403)
			console.info('Channel details not available (no access to this channel):', channelId)
		else
			console.error('Failed to fetch channel details:', error)
	}
}
